"use client";

/**
 * "Initial setup" accordion. Collapsed: a peek grid of what was configured
 * (vendor, product, problem, key inputs…). Expanded: the initial assumption
 * values from the v1 snapshot plus the same intake fields, attributed to the
 * opportunity creator and creation date. Snapshot summary by design — the
 * data model has no per-step intake events (see spec).
 * Ported from the design's `.acc` + `.setup-peek` classes.
 */

import { useId, useState } from "react";
import { formatDateLabel } from "../lib/time-format";
import { avatarColorClass, avatarInitials } from "./AvatarStack";
import type { HistorySetupVM } from "./history-types";

export interface HistorySetupCardProps {
  setup: HistorySetupVM;
  /** Open on first render. Default false (collapsed peek). */
  defaultOpen?: boolean;
}

function AuthorAvatar({ name }: { name: string }) {
  return (
    <span
      aria-hidden
      className={[
        "flex h-[22px] w-[22px] items-center justify-center rounded-full",
        "border-2 border-(--bg-white) text-[9.5px] font-bold text-(--fg-primary)",
        avatarColorClass(name),
      ].join(" ")}
    >
      {avatarInitials(name)}
    </span>
  );
}

function PeekRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 items-baseline gap-3">
      <span className="w-[92px] shrink-0 text-[11px] font-semibold tracking-wide text-(--fg-muted) uppercase">
        {label}
      </span>
      <span className="min-w-0 [overflow-wrap:anywhere] text-[13px] leading-snug text-(--fg-primary)">
        {value}
      </span>
    </div>
  );
}

export function HistorySetupCard({
  setup,
  defaultOpen = false,
}: HistorySetupCardProps) {
  const [open, setOpen] = useState(defaultOpen);
  const disclosureId = useId();
  const triggerId = `${disclosureId}-trigger`;
  const panelId = `${disclosureId}-panel`;

  return (
    <div
      className={[
        "mb-2 overflow-visible rounded-[3px] border bg-(--bg-white)",
        open ? "border-(--border-strong)" : "border-(--border-subtle)",
      ].join(" ")}
    >
      <button
        id={triggerId}
        type="button"
        aria-controls={panelId}
        aria-expanded={open}
        className={[
          "flex w-full flex-wrap items-center gap-x-3.5 gap-y-2 border-0 bg-transparent px-4 py-3.5",
          "cursor-pointer",
          "text-left font-[inherit]",
          "focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-(--fg-primary)",
        ].join(" ")}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="min-w-[110px] text-[14.5px] font-bold text-(--fg-primary)">
          Initial setup
        </span>
        <span className="text-[12.5px] font-medium text-(--fg-secondary)">
          {formatDateLabel(setup.createdAt)}
        </span>
        <span className="ml-auto inline-flex shrink-0 items-center gap-2.5">
          <AuthorAvatar name={setup.createdByName} />
          <span className="sr-only">Created by {setup.createdByName}</span>
          <span
            aria-hidden
            className={[
              "text-(--fg-muted) transition-transform",
              open ? "rotate-90" : "",
            ].join(" ")}
          >
            ›
          </span>
        </span>
      </button>
      {!open && setup.peek.length > 0 ? (
        <div className="flex flex-col gap-1.5 border-t border-(--border-subtle) px-4 pt-3 pb-4">
          {setup.peek.map((row, index) => (
            <PeekRow
              key={`${row.label}-${row.value}-${index}`}
              label={row.label}
              value={row.value}
            />
          ))}
        </div>
      ) : null}
      <section
        id={panelId}
        aria-labelledby={triggerId}
        hidden={!open}
        className="border-t border-(--border-subtle) px-4 pt-3 pb-4"
      >
        <p className="mb-3 text-[12px] text-(--fg-muted)">
          Set up by {setup.createdByName} · {formatDateLabel(setup.createdAt)}
        </p>
        {setup.peek.length > 0 ? (
          <>
            <h3 className="mb-2 text-[11px] font-semibold tracking-wide text-(--fg-muted) uppercase">
              Intake
            </h3>
            <div className="mb-4 flex flex-col gap-1.5">
              {setup.peek.map((row, index) => (
                <PeekRow
                  key={`${row.label}-${row.value}-${index}`}
                  label={row.label}
                  value={row.value}
                />
              ))}
            </div>
          </>
        ) : null}
        {setup.initialAssumptions.length > 0 ? (
          <>
            <h3 className="mb-2 text-[11px] font-semibold tracking-wide text-(--fg-muted) uppercase">
              Initial assumptions
            </h3>
            <div className="flex flex-col">
              {setup.initialAssumptions.map((row, index) => (
                <div
                  key={`${row.label}-${row.value}-${index}`}
                  className="flex min-w-0 items-baseline justify-between gap-3.5 border-b border-(--border-subtle) py-2 last:border-b-0"
                >
                  <span className="min-w-0 [overflow-wrap:anywhere] text-[13px] text-(--fg-primary)">
                    {row.label}
                  </span>
                  <span className="max-w-[50%] min-w-0 [overflow-wrap:anywhere] text-right text-[13px] font-bold text-(--fg-primary)">
                    {row.value}
                  </span>
                </div>
              ))}
            </div>
          </>
        ) : null}
        {setup.peek.length === 0 && setup.initialAssumptions.length === 0 ? (
          <p className="text-[12.5px] text-(--fg-muted) italic">
            No intake snapshot recorded for this opportunity.
          </p>
        ) : null}
      </section>
    </div>
  );
}
